import { useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { CodeBlock } from '@tiptap/extension-code-block';
import { CodeBlockLowlight } from '@tiptap/extension-code-block-lowlight';
import { Document } from '@tiptap/extension-document';
import { Text } from '@tiptap/extension-text';
import { Placeholder, UndoRedo } from '@tiptap/extensions';
import { useReduxState } from 'hooks';
import { lowlight } from 'lib/helpers';

const CodeDocument = Document.extend({
  content: 'codeBlock'
});

interface Props {
  disabled?: boolean;
  initialValue?: string;
  language?: string;
  onValueChange?: (html: string) => void;
  placeholder?: string;
  rows?: number;
  showLineNumbers?: boolean;
  value?: string;
  valueUpdateTimeout?: number;
}

const CodeInput = ({
  disabled = false,
  initialValue,
  language,
  onValueChange,
  placeholder,
  rows = 5,
  showLineNumbers = false,
  value,
  valueUpdateTimeout = 500
}: Props) => {
  const { messages, theme } = useReduxState();

  const lastEmittedRef = useRef<string | null>(null);
  const updateValueTimeout = useRef<NodeJS.Timeout>(undefined);

  const [numberOfLines, setNumberOfLines] = useState(
    value?.split('\n').length || 0
  );

  const minHeight = 18 + rows * 19;

  let editorClassName = 'vuo-code-editor';
  if (disabled) editorClassName += ' vuo-code-editor-disabled';

  const getSanitezedValue = (unsanitizedValue: string) => {
    return `<pre><code>${unsanitizedValue
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')}</code></pre>`;
  };

  const editor = useEditor({
    editable: !disabled,
    editorProps: {
      attributes: {
        class: editorClassName
      }
    },
    extensions: [
      ...(language
        ? [
            CodeBlockLowlight.configure({
              defaultLanguage: language,
              enableTabIndentation: true,
              exitOnArrowDown: false,
              exitOnTripleEnter: false,
              lowlight
            })
          ]
        : [
            CodeBlock.configure({
              exitOnArrowDown: false,
              exitOnTripleEnter: false
            })
          ]),
      CodeDocument,
      Placeholder.configure({
        placeholder: placeholder || messages.commonAddText,
        showOnlyWhenEditable: false
      }),
      Text,
      UndoRedo
    ],
    content:
      value || initialValue
        ? getSanitezedValue(value || initialValue)
        : undefined,
    immediatelyRender: false,
    parseOptions: { preserveWhitespace: 'full' },
    onUpdate({ editor }) {
      const currentValue = editor.getText();
      setNumberOfLines(currentValue.split('\n').length);

      if (currentValue === value) return;

      if (onValueChange) {
        clearTimeout(updateValueTimeout.current);
        updateValueTimeout.current = setTimeout(() => {
          lastEmittedRef.current = currentValue;
          onValueChange(currentValue);
        }, valueUpdateTimeout);
      }
    }
  });

  useEffect(() => {
    return () => {
      clearTimeout(updateValueTimeout.current);
    };
  }, []);

  useEffect(() => {
    if (!editor) return;
    editor.commands.updateAttributes('codeBlock', { language });
  }, [editor, language]);

  useEffect(() => {
    // editor options do not update dynamically, so we handle
    // changes to the disabled prop manually
    if (editor && editor.isEditable === Boolean(disabled)) {
      const editorProps = { attributes: { class: editorClassName } };
      editor.setOptions({ editorProps });
      editor.setEditable(!disabled);
    }
  }, [disabled, editor]);

  useEffect(() => {
    if (!editor) return;

    if (value === lastEmittedRef.current) {
      lastEmittedRef.current = null;
      return;
    }

    if (value !== editor.getText()) {
      const sanitizedValue = getSanitezedValue(value);
      editor.commands.setContent(sanitizedValue, { emitUpdate: false });
      setNumberOfLines(sanitizedValue.split('\n').length);
      lastEmittedRef.current = null;
    }
  }, [editor, value]);

  return (
    <div className="code">
      {showLineNumbers ? (
        <div className="line-numbers">
          {Array.from({ length: numberOfLines }).map((_, index) => (
            <div key={index} className="line">
              {index + 1}
            </div>
          ))}
        </div>
      ) : null}

      <div className="code-content">
        <EditorContent editor={editor} />
      </div>

      <style jsx global>{`
        .vuo-code-editor {
          background-color: ${theme.lightGrey};
          border: 0;
          border-radius: ${theme.inputBorderRadius};
          color: ${theme.textColor};
          font-family: inherit;
          font-size: 1.5rem;
          font-weight: 400;
          min-height: ${minHeight}px;
          outline: 0;
          overflow-x: auto;
          padding: 8px 10px;
          transition: border-color 0.2s;
          width: 100%;
        }
        .vuo-code-editor-disabled {
          background-color: ${theme.lightGreyLighter};
          border: 1px solid ${theme.lightGreyDark};
          color: ${theme.textColor};
          opacity: 0.7;
        }
        .vuo-code-editor pre {
          margin: 0;
          white-space: pre;
        }
        .ProseMirror pre.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: #adb5bd;
          pointer-events: none;
          height: 0;
        }
        .code:has(> .line-numbers) {
          align-items: stretch;
          display: flex;
          flex-shrink: 0;
        }
        .line-numbers {
          align-self: stretch;
          color: slategray;
          display: flex;
          flex-direction: column;
          font-family: inherit;
          flex-shrink: 0;
          font-size: 1.1rem;
          justify-content: space-between;
          line-height: 1.7;
          padding-right: 1em;
          text-align: right;
          user-select: none;
        }
        .code-content {
          min-width: 0;
          overflow-x: auto;
        }
      `}</style>
    </div>
  );
};

export default CodeInput;
